import { gql } from '@apollo/client';

import { GlobalStyles } from '@/types/GlobalStyles';
import { SitePage } from '@/types/SitePage';
import { SiteTemplate } from '@/types/SiteTemplate';

const SITE_SECTION_BASIC_SETTINGS_FRAGMENT = gql`
  fragment BasicSettingsFields on SiteSectionSettingUnion {
    ... on SiteSectionSetting {
      name
      type
      valueType
    }
    ... on SiteSectionSettingBoolean {
      name
      type
      boolean
    }
    ... on SiteSectionSettingDate {
      date
    }
    ... on SiteSectionSettingFloat {
      float
    }
    ... on SiteSectionSettingString {
      string
    }
  }
`;

export const SITE_SECTION_FRAGMENT = gql`
  fragment SiteSectionFields on SiteSection {
    id
    targetKey
    position
    positionDraft
    siteTemplate {
      id
    }
    templateIdentifier
    settings {
      ...BasicSettingsFields
      ... on SiteSectionSettingBackground {
        background {
          color
          image
          image_fill: imageFill
          image_overlay_color: imageOverlayColor
          use_image_overlay: useImageOverlay
        }
      }
      ... on SiteSectionSettingVideo {
        video {
          autoplay
          description
          source
          source_id: sourceId
          thumbnail {
            url
            fit
            scale
            position
          }
        }
      }
      ... on SiteSectionSettingBlockList {
        blocks {
          blockType
          settings {
            ...BasicSettingsFields
          }
        }
      }
    }
    settingsDraft {
      ...BasicSettingsFields
      ... on SiteSectionSettingBackground {
        background {
          color
          image
          image_fill: imageFill
          image_overlay_color: imageOverlayColor
          use_image_overlay: useImageOverlay
        }
      }
      ... on SiteSectionSettingVideo {
        video {
          autoplay
          description
          source
          source_id: sourceId
          thumbnail {
            url
            fit
            scale
            position
          }
        }
      }
      ... on SiteSectionSettingBlockList {
        blocks {
          blockType
          settings {
            ...BasicSettingsFields
            ... on SiteSectionSettingBackground {
              background {
                color
                image
                image_fill: imageFill
                image_overlay_color: imageOverlayColor
                use_image_overlay: useImageOverlay
              }
            }
            ... on SiteSectionSettingVideo {
              video {
                autoplay
                description
                source
                source_id: sourceId
                thumbnail {
                  url
                  fit
                  scale
                  position
                }
              }
            }
          }
        }
      }
    }
  }

  ${SITE_SECTION_BASIC_SETTINGS_FRAGMENT}
`;

const SITE_LAYOUT_FRAGMENT = gql`
  fragment SiteLayoutFields on SiteLayout {
    id
    siteTemplate {
      id
    }
    templateIdentifier
    siteSections {
      nodes {
        ...SiteSectionFields
      }
    }
  }

  ${SITE_SECTION_FRAGMENT}
`;

// product.id is used to identify and set up routing for Digital Downloads and Live Events
// product.productableId is used for Courses
const SITE_PAGE_FRAGMENT = gql`
  fragment SitePageFields on SitePage {
    id
    name
    pageType
    slug
    siteTemplate {
      id
    }
    templateIdentifier
    product {
      id
      slug
      productableType
      productableId
    }
    siteLayout {
      ...SiteLayoutFields
    }
    siteSections {
      nodes {
        ...SiteSectionFields
      }
    }
  }

  ${SITE_LAYOUT_FRAGMENT}
  ${SITE_SECTION_FRAGMENT}
`;

const SITE_TEMPLATE_ASSET_FRAGMENT = gql`
  fragment SiteTemplateAssetFields on SiteTemplateAsset {
    fileName
    url
  }
`;

const SITE_TEMPLATE_PAGE_FRAGMENT = gql`
  fragment SiteTemplatePageFields on SiteTemplatePage {
    identifier
    liquidMarkup
    liquidMarkupForHead
    liquidMarkupForFooter
    schema
    siteTemplateVersion {
      id
    }
  }
`;

const SITE_TEMPLATE_LAYOUT_FRAGMENT = gql`
  fragment SiteTemplateLayoutFields on SiteTemplateLayout {
    identifier
    liquidMarkup
    liquidMarkupForHead
    liquidMarkupForFooter
    schema
    siteTemplateVersion {
      id
    }
  }
`;

const SITE_TEMPLATE_SECTION_FRAGMENT = gql`
  fragment SiteTemplateSectionFields on SiteTemplateSection {
    identifier
    graphqlQuery
    liquidMarkup
    liquidMarkupForHead
    liquidMarkupForFooter
    name
    schema
    siteTemplateVersion {
      id
    }
  }
`;

const SITE_TEMPLATE_SNIPPET_FRAGMENT = gql`
  fragment SiteTemplateSnippetFields on SiteTemplateSnippet {
    identifier
    liquidMarkup
  }
`;

const SITE_TEMPLATE_VERSION_FRAGMENT = gql`
  fragment SiteTemplateVersionFields on SiteTemplateVersion {
    id
    version
    liquidMarkupForHead
    liquidMarkupForFooter
    templateAssets {
      nodes {
        ...SiteTemplateAssetFields
      }
    }
    templatePages {
      nodes {
        ...SiteTemplatePageFields
      }
    }
    templateLayouts {
      nodes {
        ...SiteTemplateLayoutFields
      }
    }
    templateSections {
      nodes {
        ...SiteTemplateSectionFields
      }
    }
    templateSnippets {
      nodes {
        ...SiteTemplateSnippetFields
      }
    }
  }

  ${SITE_TEMPLATE_ASSET_FRAGMENT}
  ${SITE_TEMPLATE_PAGE_FRAGMENT}
  ${SITE_TEMPLATE_LAYOUT_FRAGMENT}
  ${SITE_TEMPLATE_SECTION_FRAGMENT}
  ${SITE_TEMPLATE_SNIPPET_FRAGMENT}
`;

const SITE_TEMPLATE_FRAGMENT = gql`
  fragment SiteTemplateFields on SiteTemplate {
    id
    identifier
    name
    currentVersion {
      ...SiteTemplateVersionFields
    }
  }

  ${SITE_TEMPLATE_VERSION_FRAGMENT}
`;

export const Preview = gql`
  query Preview($sitePageId: ID!) {
    sitePage(id: $sitePageId) {
      ...SitePageFields
    }
    site {
      siteTemplates {
        nodes {
          ...SiteTemplateFields
        }
      }
      globalStyles {
        accentBgColor
        accentCtaBgColor
        accentCtaTextColor
        accentTextColor
        bodyFontWeight
        buttonBorderRadius
        buttonFontWeight
        ctaBgColor
        ctaTextColor
        inputBorderColor
        inputBorderRadius
        inputErrorColor
        inputTextColor
        primaryBgColor
        primaryFontFamily
        primaryTextColor
        promotionBadgeBgColor
        promotionBadgeTextColor
        promotionTextColor
        secondaryBgColor
        secondaryCtaBgColor
        secondaryCtaTextColor
        secondaryFontFamily
        secondaryTextColor
        subtitleFontWeight
        surfaceBorderRadius
        titleFontWeight
      }
    }
  }

  ${SITE_PAGE_FRAGMENT}
  ${SITE_TEMPLATE_FRAGMENT}
`;

export type PreviewQueryData = {
  sitePage: SitePage;
  site: {
    globalStyles: GlobalStyles | null;
    siteTemplates: {
      nodes: SiteTemplate[] | null;
    } | null;
  } | null;
};

export type PreviewQueryVariables = {
  sitePageId: string | number;
};
